import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, InputGroup, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import axiosInstance from 'axiosInstance';
import KanbanProvider from 'components/app/kanban/KanbanProvider';
import KanbanContainer from 'components/app/kanban/KanbanContainer';
import SalesPlanSelector from './components/SalesPlanSelector';
import SalesQuickCreateModal from './components/SalesQuickCreateModal';
import { BILLING_PERIODS, getDefaultBillingPeriod, getPlanQuote, getPromoBillingPeriodLabelKey, isPlanBillingPeriodEnabled } from './salesPricing';

const initialActivityForm = {
  activity_type: 'note',
  subject: '',
  description: '',
  next_followup_at: ''
};

const SalesPipelinePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stages, setStages] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [promoCampaigns, setPromoCampaigns] = useState([]);
  const [conversionOptions, setConversionOptions] = useState({ plans: [], users: [] });
  const [conversionOptionsLoading, setConversionOptionsLoading] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [savingMove, setSavingMove] = useState(false);
  const [savingConvert, setSavingConvert] = useState(false);
  const [savingActivity, setSavingActivity] = useState(false);
  const [savingPromo, setSavingPromo] = useState(false);
  const [moveTriggeredByDrag, setMoveTriggeredByDrag] = useState(false);
  const [selectedPromoCampaignId, setSelectedPromoCampaignId] = useState('');
  const [moveForm, setMoveForm] = useState({
    pipeline_stage_id: '',
    next_followup_at: '',
    note: ''
  });
  const [convertForm, setConvertForm] = useState({
    billing_email: '',
    invite_email: '',
    plan_id: '',
    billing_period: BILLING_PERIODS.MONTHLY,
    onboarding_owner_user_id: '',
    onboarding_due_days: '1',
    onboarding_note: ''
  });
  const [activityForm, setActivityForm] = useState(initialActivityForm);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [salesMetadata, setSalesMetadata] = useState({
    segments: [],
    pain_points: [],
    plans_to_offer: []
  });
  const [interestFilter, setInterestFilter] = useState('');
  const [followupFilter, setFollowupFilter] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [savingRowId, setSavingRowId] = useState('');
  const [quickCreateMode, setQuickCreateMode] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadOpenPipeline = async currentSearchQuery => {
    const [stagesResponse, opportunitiesResponse, companiesResponse, metadataResponse, promoCampaignsResponse] = await Promise.all([
      axiosInstance.get('sales/pipeline/stages/'),
      axiosInstance.get('sales/opportunities/', {
        params: { status: 'open', search: currentSearchQuery || undefined }
      }),
      axiosInstance.get('sales/companies/'),
      axiosInstance.get('sales/opportunities/metadata/'),
      axiosInstance.get('sales/promo/campaigns/')
    ]);

    return {
      stages: stagesResponse.data?.stages || [],
      opportunities: opportunitiesResponse.data?.opportunities || [],
      companies: companiesResponse.data?.companies || [],
      promoCampaigns: promoCampaignsResponse.data?.campaigns || [],
      metadata: {
        segments: metadataResponse.data?.segments || [],
        pain_points: metadataResponse.data?.pain_points || [],
        plans_to_offer: metadataResponse.data?.plans_to_offer || []
      }
    };
  };

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await loadOpenPipeline(searchQuery);

        if (!mounted) return;

        setStages(data.stages);
        setOpportunities(data.opportunities);
        setCompanies(data.companies);
        setPromoCampaigns(data.promoCampaigns);
        setSelectedPromoCampaignId(data.promoCampaigns[0] ? String(data.promoCampaigns[0].id) : '');
        setSalesMetadata(data.metadata);
      } catch (requestError) {
        if (!mounted) return;
        setError(requestError?.response?.data?.error || t('SALES.ERROR_LOAD_PIPELINE'));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();
    return () => {
      mounted = false;
    };
  }, [t, searchQuery]);

  const escapeHtml = value => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const formatFollowupLabel = value => {
    if (!value) return t('SALES.NO_NEXT_STEP');
    return new Date(value).toLocaleString();
  };

  const normalizeDateTimeLocal = value => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
  };

  const openMoveModal = item => {
    const fallbackStage = stages.find(stage => stage.id !== item.pipeline_stage_id);
    setSelectedOpportunity(item);
    setMoveForm({
      pipeline_stage_id: String(fallbackStage ? fallbackStage.id : ''),
      next_followup_at: normalizeDateTimeLocal(item.next_followup_at),
      note: ''
    });
    setShowMoveModal(true);
    setError('');
    setSuccess('');
    setMoveTriggeredByDrag(false);
  };

  const closeMoveModal = async () => {
    setShowMoveModal(false);
    if (moveTriggeredByDrag) {
      setLoading(true);
      try {
        const data = await loadOpenPipeline(searchQuery);
        setStages(data.stages);
        setOpportunities(data.opportunities);
        setCompanies(data.companies);
        setPromoCampaigns(data.promoCampaigns);
        setSalesMetadata(data.metadata);
      } catch (requestError) {
        setError(requestError?.response?.data?.error || t('SALES.ERROR_LOAD_PIPELINE'));
      } finally {
        setLoading(false);
      }
    }
    setMoveTriggeredByDrag(false);
  };

  const submitMoveStage = async event => {
    event.preventDefault();
    if (!selectedOpportunity) return;
    if (!moveForm.pipeline_stage_id) {
      setError(t('SALES.ERROR_SELECT_STAGE'));
      return;
    }

    setSavingMove(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        pipeline_stage_id: Number(moveForm.pipeline_stage_id),
        note: moveForm.note || ''
      };
      if (moveForm.next_followup_at) {
        payload.next_followup_at = moveForm.next_followup_at;
      }

      const response = await axiosInstance.post(
        `sales/opportunities/${selectedOpportunity.id}/move-stage/`,
        payload
      );
      const updated = response.data?.opportunity;
      if (updated) {
        setOpportunities(previous => previous.map(item => (
          item.id === updated.id ? updated : item
        )));
      }
      setShowMoveModal(false);
      setMoveTriggeredByDrag(false);
      setSuccess(t('SALES.MOVE_STAGE_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_MOVE_STAGE'));
    } finally {
      setSavingMove(false);
    }
  };

  const handleCardMoved = async ({ destinationColumn, movedItem }) => {
    if (!destinationColumn || !movedItem) {
      return;
    }

    const destinationStageId = Number(destinationColumn.id);
    if (!destinationStageId || Number(movedItem.pipeline_stage_id) === destinationStageId) {
      return;
    }

    setError('');
    setSuccess('');
    const originalOpportunity = opportunities.find(item => Number(item.id) === Number(movedItem.id));
    setSelectedOpportunity(originalOpportunity ? { ...originalOpportunity, pipeline_stage_id: destinationStageId } : { ...movedItem, pipeline_stage_id: destinationStageId });
    setMoveForm({
      pipeline_stage_id: String(destinationStageId),
      next_followup_at: normalizeDateTimeLocal(originalOpportunity?.next_followup_at || movedItem.next_followup_at),
      note: ''
    });
    setMoveTriggeredByDrag(true);
    setShowMoveModal(true);
  };

  const openActivityModal = item => {
    setSelectedOpportunity(item);
    setActivityForm({
      ...initialActivityForm,
      next_followup_at: normalizeDateTimeLocal(item.next_followup_at)
    });
    setShowActivityModal(true);
    setError('');
    setSuccess('');
  };

  const submitActivity = async event => {
    event.preventDefault();
    if (!selectedOpportunity) return;

    setSavingActivity(true);
    setError('');
    setSuccess('');
    try {
      await axiosInstance.post('sales/activities/', {
        company_id: selectedOpportunity.company_id,
        opportunity_id: selectedOpportunity.id,
        activity_type: activityForm.activity_type,
        subject: activityForm.subject,
        description: activityForm.description,
        next_followup_at: activityForm.next_followup_at || undefined
      });
      if (activityForm.next_followup_at) {
        setOpportunities(previous => previous.map(item => (
          item.id === selectedOpportunity.id
            ? {
                ...item,
                next_followup_at: activityForm.next_followup_at,
                last_contact_at: new Date().toISOString()
              }
            : item
        )));
      }
      setShowActivityModal(false);
      setActivityForm(initialActivityForm);
      setSuccess(t('SALES.ACTIVITY_CREATED_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_CREATE_ACTIVITY'));
    } finally {
      setSavingActivity(false);
    }
  };

  const openPromoModal = item => {
    setSelectedOpportunity(item);
    setSelectedPromoCampaignId(promoCampaigns[0] ? String(promoCampaigns[0].id) : '');
    setError('');
    setSuccess('');
    setShowPromoModal(true);
  };

  const submitPromoAssignment = async event => {
    event.preventDefault();
    if (!selectedOpportunity || !selectedPromoCampaignId) return;

    setSavingPromo(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post(`sales/opportunities/${selectedOpportunity.id}/promo/`, {
        action: 'assign',
        campaign_id: Number(selectedPromoCampaignId)
      });
      const promo = response.data?.promo || null;
      setOpportunities(previous => previous.map(item => (
        item.id === selectedOpportunity.id ? { ...item, promo } : item
      )));
      setShowPromoModal(false);
      setSuccess(t('SALES.PROMO_ASSIGNED_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_ASSIGN_PROMO'));
    } finally {
      setSavingPromo(false);
    }
  };

  const openConvertModal = item => {
    setSelectedOpportunity(item);
    const firstPlan = conversionOptions.plans[0] || null;
    const firstUser = conversionOptions.users[0] || null;
    setConvertForm({
      billing_email: '',
      invite_email: '',
      plan_id: firstPlan ? String(firstPlan.id) : '',
      billing_period: getDefaultBillingPeriod(item?.promo, firstPlan),
      onboarding_owner_user_id: firstUser ? String(firstUser.id) : '',
      onboarding_due_days: '1',
      onboarding_note: ''
    });
    setShowConvertModal(true);
    setError('');
    setSuccess('');

    setConversionOptionsLoading(true);
    axiosInstance
      .get('sales/opportunities/conversion-options/')
      .then(response => {
        const plans = response.data?.plans || [];
        const users = response.data?.users || [];
        setConversionOptions({ plans, users });
        const defaultPlan = plans[0] || null;
        const defaultUser = users[0] || null;
        setConvertForm(previous => ({
          ...previous,
          plan_id: previous.plan_id || (defaultPlan ? String(defaultPlan.id) : ''),
          billing_period: getDefaultBillingPeriod(item?.promo, defaultPlan),
          onboarding_owner_user_id: previous.onboarding_owner_user_id || (defaultUser ? String(defaultUser.id) : '')
        }));
      })
      .catch(() => {
        setConversionOptions({ plans: [], users: [] });
      })
      .finally(() => setConversionOptionsLoading(false));
  };

  const submitConvert = async event => {
    event.preventDefault();
    if (!selectedOpportunity) return;

    setSavingConvert(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        billing_email: convertForm.billing_email || undefined,
        invite_email: convertForm.invite_email || undefined,
        plan_id: convertForm.plan_id ? Number(convertForm.plan_id) : undefined,
        billing_period: convertForm.billing_period,
        onboarding_owner_user_id: convertForm.onboarding_owner_user_id ? Number(convertForm.onboarding_owner_user_id) : undefined,
        onboarding_due_days: convertForm.onboarding_due_days ? Number(convertForm.onboarding_due_days) : undefined,
        onboarding_note: convertForm.onboarding_note || undefined
      };
      await axiosInstance.post(`sales/opportunities/${selectedOpportunity.id}/convert/`, payload);
      setOpportunities(previous => previous.filter(item => item.id !== selectedOpportunity.id));
      setShowConvertModal(false);
      setSuccess(t('SALES.CONVERT_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_CONVERT_OPPORTUNITY'));
    } finally {
      setSavingConvert(false);
    }
  };

  const filteredOpportunities = useMemo(() => {
    return opportunities.filter(item => {
      if (interestFilter && item.interest_level !== interestFilter) return false;
      if (segmentFilter && item.segment !== segmentFilter) return false;
      if (followupFilter === 'overdue') {
        return item.next_followup_at && new Date(item.next_followup_at) < new Date();
      }
      if (followupFilter === 'missing') {
        return !item.next_followup_at;
      }
      return true;
    });
  }, [followupFilter, interestFilter, opportunities, segmentFilter]);

  const selectedPromoCampaign = useMemo(
    () => promoCampaigns.find(item => String(item.id) === String(selectedPromoCampaignId)) || null,
    [promoCampaigns, selectedPromoCampaignId]
  );
  const selectedPlan = useMemo(
    () => conversionOptions.plans.find(item => String(item.id) === String(convertForm.plan_id)) || null,
    [conversionOptions.plans, convertForm.plan_id]
  );
  const selectedPlanQuote = useMemo(
    () => getPlanQuote(selectedPlan, convertForm.billing_period, selectedOpportunity?.promo),
    [convertForm.billing_period, selectedOpportunity?.promo, selectedPlan]
  );

  useEffect(() => {
    if (selectedPlan && convertForm.billing_period === BILLING_PERIODS.YEARLY && !isPlanBillingPeriodEnabled(selectedPlan, BILLING_PERIODS.YEARLY)) {
      setConvertForm(previous => ({ ...previous, billing_period: BILLING_PERIODS.MONTHLY }));
    }
  }, [convertForm.billing_period, selectedPlan]);

  const boardSummary = useMemo(() => {
    const total = filteredOpportunities.length;
    const overdue = filteredOpportunities.filter(item => item.next_followup_at && new Date(item.next_followup_at) < new Date()).length;
    const hot = filteredOpportunities.filter(item => item.interest_level === 'hot').length;
    const withoutNextStep = filteredOpportunities.filter(item => !item.next_followup_at).length;
    const promoExpiring = filteredOpportunities.filter(item => item.promo?.expires_soon).length;
    const closingSoon = filteredOpportunities.filter(item => {
      if (!item.expected_close_date) return false;
      const closeDate = new Date(item.expected_close_date);
      const now = new Date();
      const diffDays = Math.ceil((closeDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= 7;
    }).length;
    return { total, overdue, hot, withoutNextStep, closingSoon, promoExpiring };
  }, [filteredOpportunities]);

  const getOpportunityAttentionState = item => {
    if (!item.next_followup_at) return 'missing';
    const due = new Date(item.next_followup_at);
    const now = new Date();
    if (due < now) return 'overdue';
    if (due.toDateString() === now.toDateString()) return 'today';
    return 'future';
  };

  const getAttentionClassName = item => {
    const state = getOpportunityAttentionState(item);
    if (state === 'overdue') return 'border-start border-4 border-danger';
    if (state === 'today') return 'border-start border-4 border-warning';
    if (state === 'missing') return 'border-start border-4 border-secondary';
    return 'border-start border-4 border-success';
  };

  const getAttentionBadge = item => {
    const state = getOpportunityAttentionState(item);
    if (state === 'overdue') return { type: 'danger', text: t('SALES.OVERDUE') };
    if (state === 'today') return { type: 'warning', text: t('SALES.DUE_TODAY') };
    if (state === 'missing') return { type: 'secondary', text: t('SALES.NO_NEXT_STEP') };
    return null;
  };

  const getStageExpectation = stage => {
    if (!stage) return '';
    if (stage.expected_next_action) {
      return stage.expected_next_action;
    }
    if (stage.requires_next_followup && stage.stale_after_days) {
      return t('SALES.STAGE_EXPECTATION_WITH_SLA', { days: stage.stale_after_days });
    }
    if (stage.requires_next_followup) {
      return t('SALES.STAGE_EXPECTATION_REQUIRES_STEP');
    }
    if (stage.stale_after_days) {
      return t('SALES.STAGE_EXPECTATION_SLA_ONLY', { days: stage.stale_after_days });
    }
    return t('SALES.STAGE_EXPECTATION_DEFAULT');
  };

  const kanbanItems = useMemo(() => {
    return stages.map(stage => {
      const stageItems = filteredOpportunities
        .filter(item => Number(item.pipeline_stage_id) === Number(stage.id))
        .map(item => ({
          ...item,
          id: Number(item.id),
          title: [
            `<strong>${escapeHtml(item.title)}</strong>`,
            `<small class="text-600 d-block mt-1">${escapeHtml(item.company_name || String(item.company_id || '-'))}</small>`,
            `<small class="d-block mt-2 ${getOpportunityAttentionState(item) === 'overdue' ? 'text-danger fw-bold' : getOpportunityAttentionState(item) === 'today' ? 'text-warning fw-bold' : getOpportunityAttentionState(item) === 'missing' ? 'text-700 fw-bold' : 'text-600'}">${escapeHtml(t('SALES.NEXT_ACTION_DATE'))}: ${escapeHtml(formatFollowupLabel(item.next_followup_at))}</small>`,
            `<small class="d-block text-600 mt-1">${escapeHtml(t('SALES.NEXT_STEP'))}: ${escapeHtml(item.next_step_summary || t('SALES.NO_NEXT_STEP'))}</small>`,
            `<small class="d-block text-600 mt-1">${escapeHtml(t('SALES.OWNER'))}: ${escapeHtml(item.owner_name || '-')}</small>`
          ].join(''),
          labels: [
            {
              type: item.interest_level === 'hot' ? 'danger' : item.interest_level === 'warm' ? 'warning' : 'primary',
              text: item.interest_level === 'hot' ? t('SALES.HOT') : item.interest_level === 'warm' ? t('SALES.WARM') : t('SALES.COLD')
            },
            ...(getAttentionBadge(item) ? [getAttentionBadge(item)] : []),
            ...(item.promo ? [{ type: 'info', text: t('SALES.HAS_PROMO_BADGE') }] : []),
            ...(item.promo?.expires_soon ? [{ type: 'warning', text: t('SALES.PROMO_EXPIRING_SOON') }] : [])
          ],
          cardClassName: `${getAttentionClassName(item)} shadow-sm`,
          disableModalOpen: true,
          hideMenu: true,
          actionButtons: [
            {
              key: `open-${item.id}`,
              label: t('SALES.OPEN_SHORT'),
              variant: 'outline-secondary',
              onClick: () => navigate(`/sales/opportunity/${item.id}`)
            },
            {
              key: `activity-${item.id}`,
              label: t('SALES.LOG_ACTIVITY_SHORT'),
              variant: 'outline-primary',
              onClick: () => openActivityModal(item)
            },
            {
              key: `task-${item.id}`,
              label: t('SALES.TASK_SHORT'),
              variant: 'outline-dark',
              onClick: () => openQuickTaskForOpportunity(item)
            },
            {
              key: `promo-${item.id}`,
              label: t('SALES.PROMO_SHORT'),
              variant: 'outline-warning',
              onClick: () => openPromoModal(item)
            }
          ]
        }));

      return {
        id: Number(stage.id),
        name: `${stage.name} (${stageItems.length})`,
        items: stageItems
      };
    });
  }, [stages, filteredOpportunities, t, navigate]);

  const kanbanKey = useMemo(
    () => filteredOpportunities.map(item => `${item.id}:${item.pipeline_stage_id}`).join('|'),
    [filteredOpportunities]
  );

  const handleCreated = payload => {
    if (payload?.type === 'opportunity' && payload.opportunity) {
      setOpportunities(previous => [payload.opportunity, ...previous]);
    }
    if (payload?.type === 'company' && payload.company) {
      setCompanies(previous => [payload.company, ...previous]);
    }
  };

  const updateOpportunityInline = async (opportunityId, patch) => {
    setSavingRowId(String(opportunityId));
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.patch(`sales/opportunities/${opportunityId}/`, patch);
      const updated = response.data?.opportunity;
      if (updated) {
        setOpportunities(previous => previous.map(item => (item.id === updated.id ? updated : item)));
      }
      setSuccess(t('SALES.INLINE_UPDATED_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_INLINE_UPDATE'));
    } finally {
      setSavingRowId('');
    }
  };

  const segmentOptions = useMemo(() => salesMetadata.segments || [], [salesMetadata.segments]);
  const painPointOptions = useMemo(() => salesMetadata.pain_points || [], [salesMetadata.pain_points]);
  const planOptions = useMemo(() => salesMetadata.plans_to_offer || [], [salesMetadata.plans_to_offer]);
  const getOptionsWithCurrentValue = (options, currentValue) => {
    if (!currentValue) return options;
    if (options.some(option => option.value === currentValue)) return options;
    return [{ value: currentValue, label: currentValue }, ...options];
  };

  const sortedTableOpportunities = useMemo(() => {
    return [...filteredOpportunities].sort((a, b) => {
      const aDate = a.next_followup_at ? new Date(a.next_followup_at).getTime() : Number.MAX_SAFE_INTEGER;
      const bDate = b.next_followup_at ? new Date(b.next_followup_at).getTime() : Number.MAX_SAFE_INTEGER;
      return aDate - bDate;
    });
  }, [filteredOpportunities]);

  const getFollowupClassName = item => {
    if (!item.next_followup_at) return 'text-danger fw-semi-bold';
    const due = new Date(item.next_followup_at);
    const now = new Date();
    if (due < now) return 'text-danger fw-semi-bold';
    if (due.toDateString() === now.toDateString()) return 'text-warning fw-semi-bold';
    return 'text-600';
  };

  const openQuickTaskForOpportunity = item => {
    setQuickCreateMode('task');
    setSelectedOpportunity(item);
  };

  return (
    <>
      <PageHeader title={t('SALES.PIPELINE')} className="mb-3" />
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      {loading ? (
        <Card className="p-4 text-center">
          <Spinner animation="border" />
        </Card>
      ) : (
        <>
          <Card className="mb-3 border-primary-subtle">
            <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <div className="text-primary fw-semi-bold">{t('SALES.PIPELINE_DISCIPLINE')}</div>
                <div className="text-600 fs--1">{t('SALES.PIPELINE_DISCIPLINE_HELP')}</div>
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <ButtonGroup size="sm">
                  <Button variant={viewMode === 'table' ? 'primary' : 'outline-primary'} onClick={() => setViewMode('table')}>
                    {t('SALES.TABLE_VIEW')}
                  </Button>
                  <Button variant={viewMode === 'board' ? 'primary' : 'outline-primary'} onClick={() => setViewMode('board')}>
                    {t('SALES.BOARD_VIEW')}
                  </Button>
                </ButtonGroup>
                <Button size="sm" onClick={() => setQuickCreateMode('opportunity')}>{t('SALES.NEW_OPPORTUNITY')}</Button>
                <Button size="sm" variant="outline-primary" onClick={() => setQuickCreateMode('contact')}>{t('SALES.NEW_CONTACT')}</Button>
                <Button size="sm" variant="outline-dark" onClick={() => setQuickCreateMode('company')}>{t('SALES.NEW_COMPANY')}</Button>
              </div>
            </Card.Body>
          </Card>

          {viewMode !== 'board' ? (
            <>
              <Row className="g-3 mb-3">
                <Col md={6} xl={2}>
                  <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.OPEN_OPPORTUNITIES')}</div><div className="fs-2 fw-bold">{boardSummary.total}</div></Card.Body></Card>
                </Col>
                <Col md={6} xl={2}>
                  <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.HOT_OPPORTUNITIES')}</div><div className="fs-2 fw-bold text-warning">{boardSummary.hot}</div></Card.Body></Card>
                </Col>
                <Col md={6} xl={2}>
                  <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.OVERDUE_FOLLOWUPS')}</div><div className="fs-2 fw-bold text-danger">{boardSummary.overdue}</div></Card.Body></Card>
                </Col>
                <Col md={6} xl={2}>
                  <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.NO_NEXT_STEP')}</div><div className="fs-2 fw-bold text-info">{boardSummary.withoutNextStep}</div></Card.Body></Card>
                </Col>
                <Col md={6} xl={2}>
                  <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.CLOSING_THIS_WEEK')}</div><div className="fs-2 fw-bold text-success">{boardSummary.closingSoon}</div></Card.Body></Card>
                </Col>
                <Col md={6} xl={2}>
                  <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.PROMO_EXPIRING_SOON')}</div><div className="fs-2 fw-bold text-warning">{boardSummary.promoExpiring}</div></Card.Body></Card>
                </Col>
              </Row>

              <Card className="mb-3">
                <Card.Body>
                  <Row className="g-3">
                    <Col lg={5}>
                      <InputGroup>
                        <InputGroup.Text>{t('SALES.SEARCH')}</InputGroup.Text>
                        <Form.Control
                          value={searchInput}
                          onChange={event => setSearchInput(event.target.value)}
                          placeholder={t('SALES.SEARCH_PIPELINE_PLACEHOLDER')}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={4} lg={3}>
                      <Form.Select value={interestFilter} onChange={event => setInterestFilter(event.target.value)}>
                        <option value="">{t('SALES.ALL_TEMPERATURES')}</option>
                        <option value="hot">{t('SALES.HOT')}</option>
                        <option value="warm">{t('SALES.WARM')}</option>
                        <option value="cold">{t('SALES.COLD')}</option>
                      </Form.Select>
                    </Col>
                    <Col md={4} lg={3}>
                      <Form.Select value={followupFilter} onChange={event => setFollowupFilter(event.target.value)}>
                        <option value="">{t('SALES.ALL_FOLLOWUPS')}</option>
                        <option value="overdue">{t('SALES.OVERDUE_FOLLOWUPS')}</option>
                        <option value="missing">{t('SALES.NO_NEXT_STEP')}</option>
                      </Form.Select>
                    </Col>
                    <Col md={4} lg={2}>
                      <Form.Select value={segmentFilter} onChange={event => setSegmentFilter(event.target.value)}>
                        <option value="">{t('SALES.ALL_SEGMENTS')}</option>
                        {segmentOptions.map(item => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={4} lg={1} className="d-flex justify-content-end">
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setSearchInput('');
                          setInterestFilter('');
                          setFollowupFilter('');
                          setSegmentFilter('');
                        }}
                      >
                        {t('SALES.CLEAR')}
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Body className="d-flex flex-wrap gap-2">
                  {stages.map(stage => {
                    const count = filteredOpportunities.filter(item => Number(item.pipeline_stage_id) === Number(stage.id)).length;
                    return (
                      <Badge key={stage.id} bg="soft-primary" text="primary" className="px-3 py-2">
                        {stage.name}: {count}
                      </Badge>
                    );
                  })}
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Body className="d-flex flex-wrap gap-3">
                  {stages.map(stage => (
                    <div key={stage.id} className="border rounded-3 px-3 py-2 bg-white" style={{ minWidth: 220 }}>
                      <div className="fw-semi-bold">{stage.name}</div>
                      <div className="fs--2 text-600">{getStageExpectation(stage)}</div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </>
          ) : null}
          {viewMode === 'board' ? (
            <div className="sales-pipeline-board">
              <KanbanProvider
                key={kanbanKey}
                initialData={{ kanbanItems, kanbanModal: { show: false, modalContent: {} } }}
              >
                <KanbanContainer
                  showAddList={false}
                  showModal={false}
                  allowAddCard={false}
                  menuEnabled={false}
                  onCardMoved={handleCardMoved}
                />
              </KanbanProvider>
            </div>
          ) : (
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span className="fw-semi-bold">{t('SALES.LEADS_TABLE')}</span>
                <Badge bg="soft-primary" text="primary">{sortedTableOpportunities.length}</Badge>
              </Card.Header>
              <Card.Body className="pt-2">
                <Table responsive hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>{t('SALES.COMPANY')}</th>
                      <th>{t('SALES.CONTACT')}</th>
                      <th>{t('SALES.STATUS')}</th>
                      <th>{t('SALES.TEMPERATURE')}</th>
                      <th>{t('SALES.LAST_CONTACT')}</th>
                      <th>{t('SALES.NEXT_STEP')}</th>
                      <th>{t('SALES.NEXT_ACTION_DATE')}</th>
                      <th>{t('SALES.OWNER')}</th>
                      <th>{t('SALES.SEGMENT')}</th>
                      <th>{t('SALES.PAIN_POINTS')}</th>
                      <th>{t('SALES.PLAN_TO_OFFER')}</th>
                      <th>{t('SALES.NOTES_SHORT')}</th>
                      <th className="text-end">{t('SALES.TABLE_ACTIONS')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTableOpportunities.length ? sortedTableOpportunities.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="fw-semi-bold">{item.company_name}</div>
                          <div className="text-600 fs--2">{item.company_country || '-'} · {item.company_industry || '-'}</div>
                        </td>
                        <td>
                          <div className="fw-semi-bold">{item.primary_contact_name || '-'}</div>
                          <div className="d-flex gap-2 flex-wrap mt-1">
                            {item.primary_contact_phone ? (
                              <Button size="sm" variant="link" className="p-0" href={`tel:${item.primary_contact_phone}`}>tel</Button>
                            ) : null}
                            {item.primary_contact_whatsapp ? (
                              <Button size="sm" variant="link" className="p-0" href={`https://wa.me/${String(item.primary_contact_whatsapp).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer">wa</Button>
                            ) : null}
                            {item.primary_contact_email ? (
                              <Button size="sm" variant="link" className="p-0" href={`mailto:${item.primary_contact_email}`}>mail</Button>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <div className="fw-semi-bold">{item.pipeline_stage_name || t('SALES.NO_STAGE')}</div>
                          <Badge bg={item.status === 'won' ? 'success' : item.status === 'lost' ? 'danger' : 'info'}>{item.status}</Badge>
                          {item.promo ? <Badge bg="soft-warning" text="warning" className="ms-2">{t('SALES.HAS_PROMO_BADGE')}</Badge> : null}
                          {item.promo?.expires_soon ? <Badge bg="soft-danger" text="danger" className="ms-2">{t('SALES.PROMO_EXPIRING_SOON')}</Badge> : null}
                        </td>
                        <td style={{ minWidth: 140 }}>
                          <Form.Select
                            size="sm"
                            value={item.interest_level || 'cold'}
                            disabled={savingRowId === String(item.id)}
                            className={item.interest_level === 'hot' ? 'border-danger text-danger' : item.interest_level === 'warm' ? 'border-warning text-warning' : 'border-primary text-primary'}
                            onChange={event => updateOpportunityInline(item.id, { interest_level: event.target.value })}
                          >
                            <option value="cold">{t('SALES.COLD')}</option>
                            <option value="warm">{t('SALES.WARM')}</option>
                            <option value="hot">{t('SALES.HOT')}</option>
                          </Form.Select>
                        </td>
                        <td>{item.last_contact_at ? new Date(item.last_contact_at).toLocaleDateString() : '-'}</td>
                        <td style={{ minWidth: 220 }}>
                          <Form.Control
                            size="sm"
                            defaultValue={item.next_step_summary || ''}
                            placeholder={t('SALES.NEXT_STEP_PLACEHOLDER')}
                            disabled={savingRowId === String(item.id)}
                            onBlur={event => {
                              if ((item.next_step_summary || '') !== event.target.value) {
                                updateOpportunityInline(item.id, { next_step_summary: event.target.value });
                              }
                            }}
                          />
                        </td>
                        <td style={{ minWidth: 200 }}>
                          <Form.Control
                            size="sm"
                            type="datetime-local"
                            defaultValue={normalizeDateTimeLocal(item.next_followup_at)}
                            className={getFollowupClassName(item)}
                            disabled={savingRowId === String(item.id)}
                            onBlur={event => {
                              if (normalizeDateTimeLocal(item.next_followup_at) !== event.target.value) {
                                updateOpportunityInline(item.id, { next_followup_at: event.target.value });
                              }
                            }}
                          />
                        </td>
                        <td>{item.owner_name || '-'}</td>
                        <td style={{ minWidth: 150 }}>
                          <Form.Select
                            size="sm"
                            value={item.segment || ''}
                            disabled={savingRowId === String(item.id)}
                            onChange={event => updateOpportunityInline(item.id, { segment: event.target.value })}
                          >
                            <option value="">{t('SALES.SELECT_ONE')}</option>
                            {getOptionsWithCurrentValue(segmentOptions, item.segment).map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </Form.Select>
                        </td>
                        <td style={{ minWidth: 180 }}>
                          <Form.Select
                            size="sm"
                            value={item.pain_points_summary || ''}
                            disabled={savingRowId === String(item.id)}
                            onChange={event => updateOpportunityInline(item.id, { pain_points_summary: event.target.value })}
                          >
                            <option value="">{t('SALES.SELECT_ONE')}</option>
                            {getOptionsWithCurrentValue(painPointOptions, item.pain_points_summary).map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </Form.Select>
                        </td>
                        <td style={{ minWidth: 150 }}>
                          <Form.Select
                            size="sm"
                            value={item.plan_to_offer || ''}
                            disabled={savingRowId === String(item.id)}
                            onChange={event => updateOpportunityInline(item.id, { plan_to_offer: event.target.value })}
                          >
                            <option value="">{t('SALES.SELECT_ONE')}</option>
                            {getOptionsWithCurrentValue(planOptions, item.plan_to_offer).map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </Form.Select>
                        </td>
                        <td style={{ minWidth: 220 }}>
                          <Form.Control
                            size="sm"
                            defaultValue={item.quick_notes || ''}
                            placeholder={t('SALES.NOTES_PLACEHOLDER')}
                            disabled={savingRowId === String(item.id)}
                            onBlur={event => {
                              if ((item.quick_notes || '') !== event.target.value) {
                                updateOpportunityInline(item.id, { quick_notes: event.target.value });
                              }
                            }}
                          />
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-2 flex-wrap">
                            <Button size="sm" variant="outline-secondary" onClick={() => navigate(`/sales/opportunity/${item.id}`)}>
                              {t('SALES.OPEN_DEAL')}
                            </Button>
                            <Button size="sm" variant="outline-primary" onClick={() => openQuickTaskForOpportunity(item)}>
                              {t('SALES.NEW_TASK')}
                            </Button>
                            <Button size="sm" variant="outline-dark" onClick={() => openMoveModal(item)}>
                              {t('SALES.MOVE_STAGE')}
                            </Button>
                            <Button size="sm" variant="outline-success" onClick={() => openConvertModal(item)}>
                              {t('SALES.CONVERT')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={13} className="text-center text-600 py-4">{t('SALES.EMPTY_OPPORTUNITIES')}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </>
      )}

      <SalesQuickCreateModal
        show={Boolean(quickCreateMode)}
        mode={quickCreateMode || 'opportunity'}
        onHide={() => {
          setQuickCreateMode('');
          setSelectedOpportunity(null);
        }}
        companies={companies}
        opportunities={opportunities}
        stages={stages}
        defaultCompanyId={selectedOpportunity?.company_id}
        defaultOpportunityId={selectedOpportunity?.id}
        onCreated={handleCreated}
        onRequestModeChange={setQuickCreateMode}
      />

      <Modal show={showMoveModal} onHide={closeMoveModal} centered>
        <Form onSubmit={submitMoveStage}>
          <Modal.Header closeButton>
            <Modal.Title>{t('SALES.MOVE_STAGE')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-2 fw-semi-bold">{selectedOpportunity?.title || '-'}</div>
            {moveTriggeredByDrag ? (
              <Alert variant="info" className="py-2">
                {t('SALES.POST_MOVE_GUIDANCE')}
              </Alert>
            ) : null}
            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.PIPELINE_STAGE')}</Form.Label>
              <Form.Select
                value={moveForm.pipeline_stage_id}
                onChange={event => setMoveForm(previous => ({ ...previous, pipeline_stage_id: event.target.value }))}
                required
              >
                <option value="">{t('SALES.SELECT_ONE')}</option>
                {stages.map(stage => (
                  <option key={stage.id} value={stage.id}>{stage.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.NEXT_FOLLOWUP')}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={moveForm.next_followup_at}
                onChange={event => setMoveForm(previous => ({ ...previous, next_followup_at: event.target.value }))}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>{t('SALES.NOTE')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={moveForm.note}
                onChange={event => setMoveForm(previous => ({ ...previous, note: event.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeMoveModal}>{t('SALES.CANCEL')}</Button>
            <Button type="submit" variant="primary" disabled={savingMove}>
              {savingMove ? t('SALES.SAVING') : t('SALES.SAVE_CHANGES')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showActivityModal} onHide={() => setShowActivityModal(false)} centered>
        <Form onSubmit={submitActivity}>
          <Modal.Header closeButton>
            <Modal.Title>{t('SALES.LOG_ACTIVITY')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-2 fw-semi-bold">{selectedOpportunity?.title || '-'}</div>
            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.ACTIVITY_TYPE')}</Form.Label>
              <Form.Select
                value={activityForm.activity_type}
                onChange={event => setActivityForm(previous => ({ ...previous, activity_type: event.target.value }))}
              >
                <option value="note">{t('SALES.NOTE')}</option>
                <option value="call">{t('SALES.ACTIVITY_CALL')}</option>
                <option value="meeting">{t('SALES.ACTIVITY_MEETING')}</option>
                <option value="email">{t('SALES.ACTIVITY_EMAIL')}</option>
                <option value="demo">{t('SALES.ACTIVITY_DEMO')}</option>
                <option value="proposal_sent">{t('SALES.ACTIVITY_PROPOSAL')}</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.SUBJECT')}</Form.Label>
              <Form.Control
                value={activityForm.subject}
                onChange={event => setActivityForm(previous => ({ ...previous, subject: event.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.DESCRIPTION')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={activityForm.description}
                onChange={event => setActivityForm(previous => ({ ...previous, description: event.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('SALES.NEXT_FOLLOWUP')}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={activityForm.next_followup_at}
                onChange={event => setActivityForm(previous => ({ ...previous, next_followup_at: event.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowActivityModal(false)}>{t('SALES.CANCEL')}</Button>
            <Button type="submit" variant="primary" disabled={savingActivity}>
              {savingActivity ? t('SALES.SAVING') : t('SALES.SAVE')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showPromoModal} onHide={() => setShowPromoModal(false)} centered>
        <Form onSubmit={submitPromoAssignment}>
          <Modal.Header closeButton>
            <Modal.Title>{t('SALES.ASSIGN_PROMO')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-2 fw-semi-bold">{selectedOpportunity?.title || '-'}</div>
            <Form.Group>
              <Form.Label>{t('SALES.CAMPAIGN')}</Form.Label>
              <Form.Select
                value={selectedPromoCampaignId}
                onChange={event => setSelectedPromoCampaignId(event.target.value)}
                required
              >
                <option value="">{t('SALES.SELECT_ONE')}</option>
                {promoCampaigns.filter(item => item.is_active).map(item => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {selectedPromoCampaign ? (
              <div className="mt-3 text-600 fs--1">
                <div>{selectedPromoCampaign.description || t('SALES.NO_DESCRIPTION')}</div>
                <Badge bg={selectedPromoCampaign.applicable_billing_period === 'yearly' ? 'dark' : selectedPromoCampaign.applicable_billing_period === 'monthly' ? 'info' : 'secondary'} className="mt-2">
                  {t(getPromoBillingPeriodLabelKey(selectedPromoCampaign.applicable_billing_period))}
                </Badge>
              </div>
            ) : null}
            {selectedOpportunity?.promo ? (
              <div className="mt-3 text-600 fs--1">
                {t('SALES.CURRENT_PROMO')}: {selectedOpportunity.promo.campaign_name} · {selectedOpportunity.promo.code}
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPromoModal(false)}>{t('SALES.CANCEL')}</Button>
            <Button type="submit" variant="warning" disabled={savingPromo}>
              {savingPromo ? t('SALES.SAVING') : t('SALES.ASSIGN_PROMO')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showConvertModal} onHide={() => setShowConvertModal(false)} centered>
        <Form onSubmit={submitConvert}>
          <Modal.Header closeButton>
            <Modal.Title>{t('SALES.CONVERT')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3 fw-semi-bold">{selectedOpportunity?.title || '-'}</div>
            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.BILLING_EMAIL')}</Form.Label>
              <Form.Control
                type="email"
                value={convertForm.billing_email}
                onChange={event => setConvertForm(previous => ({ ...previous, billing_email: event.target.value }))}
                placeholder="billing@company.com"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('SALES.INVITE_EMAIL')}</Form.Label>
              <Form.Control
                type="email"
                value={convertForm.invite_email}
                onChange={event => setConvertForm(previous => ({ ...previous, invite_email: event.target.value }))}
                placeholder="user@company.com"
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('SALES.PROMO')}</Form.Label>
              <Form.Control
                value={selectedOpportunity?.promo ? `${selectedOpportunity.promo.campaign_name} · ${selectedOpportunity.promo.code}` : t('SALES.NO_PROMO')}
                readOnly
                disabled
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <SalesPlanSelector
                plans={conversionOptions.plans}
                selectedPlanId={convertForm.plan_id}
                viewerCountry={selectedOpportunity?.company_country}
                onSelectPlanId={planId => {
                  const nextPlan = conversionOptions.plans.find(item => String(item.id) === String(planId));
                  setConvertForm(previous => ({
                    ...previous,
                    plan_id: planId,
                    billing_period: getDefaultBillingPeriod(selectedOpportunity?.promo, nextPlan)
                  }));
                }}
                billingPeriod={convertForm.billing_period}
                onBillingPeriodChange={value => setConvertForm(previous => ({ ...previous, billing_period: value }))}
                promo={selectedOpportunity?.promo || null}
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('SALES.ONBOARDING_OWNER')}</Form.Label>
              <Form.Select
                value={convertForm.onboarding_owner_user_id}
                onChange={event => setConvertForm(previous => ({ ...previous, onboarding_owner_user_id: event.target.value }))}
                disabled={conversionOptionsLoading}
              >
                <option value="">{t('SALES.SELECT_ONE')}</option>
                {conversionOptions.users.map(item => (
                  <option key={item.id} value={item.id}>{item.full_name || item.username}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('SALES.ONBOARDING_DUE_DAYS')}</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={30}
                value={convertForm.onboarding_due_days}
                onChange={event => setConvertForm(previous => ({ ...previous, onboarding_due_days: event.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>{t('SALES.NOTE')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={convertForm.onboarding_note}
                onChange={event => setConvertForm(previous => ({ ...previous, onboarding_note: event.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConvertModal(false)}>{t('SALES.CANCEL')}</Button>
            <Button type="submit" variant="success" disabled={savingConvert || Boolean(selectedPlanQuote && !selectedPlanQuote.isCompatible)}>
              {savingConvert ? t('SALES.SAVING') : t('SALES.CONVERT')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default SalesPipelinePage;
